import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { SectionTitle } from './premium/Decor';
import { ArrowRight, Sparkles } from 'lucide-react';
import { PremiumButton } from './premium/Decor';

/**
 * Rotating Vedic zodiac wheel — 12 rashi signs with sanskrit/hindi names
 * around a central Om mark, with a slow CSS spin.
 * Matches the cosmic-mystical aesthetic of premium astrology landing pages.
 */
const ZODIAC = [
  { hi: 'मेष',    en: 'Aries',       glyph: '♈' },
  { hi: 'वृषभ',   en: 'Taurus',      glyph: '♉' },
  { hi: 'मिथुन', en: 'Gemini',      glyph: '♊' },
  { hi: 'कर्क',   en: 'Cancer',      glyph: '♋' },
  { hi: 'सिंह',   en: 'Leo',         glyph: '♌' },
  { hi: 'कन्या',  en: 'Virgo',       glyph: '♍' },
  { hi: 'तुला',   en: 'Libra',       glyph: '♎' },
  { hi: 'वृश्चिक', en: 'Scorpio',    glyph: '♏' },
  { hi: 'धनु',    en: 'Sagittarius', glyph: '♐' },
  { hi: 'मकर',   en: 'Capricorn',   glyph: '♑' },
  { hi: 'कुंभ',   en: 'Aquarius',    glyph: '♒' },
  { hi: 'मीन',    en: 'Pisces',      glyph: '♓' },
];

const ZodiacWheelSection = ({ onOpenForm }) => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section
      data-testid="zodiac-wheel-section"
      className="relative bg-gradient-to-b from-brand-cream via-brand-ivory to-brand-cream py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isHi ? 'राशि चक्र' : 'Vedic Zodiac'}
          title={isHi ? 'आपकी राशि — आपकी तकदीर का द्वार' : 'Your Rashi — The Gateway To Your Destiny'}
          subtitle={isHi
            ? '12 राशियों का दिव्य चक्र आपके जन्म के समय की ग्रह स्थिति को दर्शाता है।'
            : 'The divine wheel of 12 zodiacs reveals the planetary geometry at the moment you were born.'}
          language={language}
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT: Animated Zodiac wheel */}
          <div className="relative flex items-center justify-center order-2 lg:order-1">
            {/* Outer glow */}
            <div className="absolute inset-0 bg-brand-gold/15 blur-3xl rounded-full" />

            {/* Wheel container */}
            <div className="relative w-[320px] h-[320px] md:w-[440px] md:h-[440px]">
              {/* Outer ring with rotating signs */}
              <div className="absolute inset-0 animate-spin-slow">
                {/* Concentric rings */}
                <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full">
                  <defs>
                    <radialGradient id="wheel-bg" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#FFF8E7" stopOpacity="1" />
                      <stop offset="70%" stopColor="#F3E5AB" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.2" />
                    </radialGradient>
                  </defs>
                  <circle cx="200" cy="200" r="195" fill="url(#wheel-bg)" stroke="#AA8825" strokeWidth="1.5" />
                  <circle cx="200" cy="200" r="170" fill="none" stroke="#AA8825" strokeWidth="0.7" strokeOpacity="0.5" />
                  <circle cx="200" cy="200" r="135" fill="none" stroke="#AA8825" strokeWidth="0.7" strokeOpacity="0.4" />
                  <circle cx="200" cy="200" r="100" fill="none" stroke="#AA8825" strokeWidth="0.7" strokeOpacity="0.3" />
                  {/* 12 spokes */}
                  {Array.from({ length: 12 }).map((_, i) => {
                    const angle = (i * 30 * Math.PI) / 180;
                    const x1 = 200 + Math.cos(angle) * 100;
                    const y1 = 200 + Math.sin(angle) * 100;
                    const x2 = 200 + Math.cos(angle) * 195;
                    const y2 = 200 + Math.sin(angle) * 195;
                    return (
                      <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke="#AA8825" strokeWidth="0.6" strokeOpacity="0.45" />
                    );
                  })}
                </svg>

                {/* 12 zodiac glyphs */}
                {ZODIAC.map((z, i) => {
                  const angle = (i * 30 - 90) * (Math.PI / 180);
                  const radius = 165;
                  const left = 50 + (Math.cos(angle) * radius) / 4.4;
                  const top = 50 + (Math.sin(angle) * radius) / 4.4;
                  return (
                    <div
                      key={i}
                      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
                      style={{ left: `${left}%`, top: `${top}%` }}
                    >
                      <div className="w-9 h-9 md:w-12 md:h-12 rounded-full bg-brand-maroon text-brand-gold-light flex items-center justify-center font-playfair text-xl md:text-2xl shadow-maroon-soft border border-brand-gold/60">
                        {z.glyph}
                      </div>
                      <span className={`mt-1 text-[9px] md:text-[11px] font-bold text-brand-maroon-dark tracking-wider ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                        {isHi ? z.hi : z.en}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Center fixed Om */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-brand-maroon to-brand-maroon-dark text-brand-gold-light flex items-center justify-center font-hindi-serif text-5xl md:text-7xl shadow-premium border-4 border-brand-gold animate-pulse-soft">
                  ॐ
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Copy */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            <p className={`text-brand-gold-dark uppercase tracking-[0.25em] text-xs sm:text-sm font-semibold mb-3 ${isHi ? 'font-hindi' : ''}`}>
              {isHi ? 'वैदिक ज्योतिष' : 'Vedic Astrology'}
            </p>
            <h3 className={`font-playfair font-bold text-3xl md:text-4xl text-brand-maroon leading-tight mb-5 ${isHi ? 'font-hindi-serif' : ''}`}>
              {isHi ? '5,000 साल पुराना ज्ञान — आपके लिए' : '5,000 Years Of Wisdom — Decoded For You'}
            </h3>
            <p className={`text-brand-text-soft text-base md:text-lg leading-relaxed mb-6 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
              {isHi
                ? 'आपकी कुंडली केवल कागज पर बने चार्ट नहीं — यह आपके जन्म के क्षण की दिव्य ब्रह्मांडीय भाषा है। हम स्वामी ग्रह, नक्षत्र, दशा-अंतर्दशा, और 16 वर्ग कुंडलियाँ का गहन विश्लेषण करते हैं।'
                : 'Your Kundli is not just a chart — it is the divine cosmic blueprint of the moment you were born. We decode lord planets, nakshatras, dasha-antardasha, and all 16 divisional charts.'}
            </p>
            <ul className="space-y-2.5 mb-7 max-w-md mx-auto lg:mx-0 text-left">
              {[
                { hi: '12 राशियाँ × 9 ग्रह × 27 नक्षत्र', en: '12 Rashis × 9 Planets × 27 Nakshatras' },
                { hi: 'विंशोत्तरी दशा 120 साल तक', en: 'Vimshottari Dasha for 120 Years' },
                { hi: '16 वर्ग कुंडली (D1 से D60)', en: '16 Divisional Charts (D1 to D60)' },
                { hi: 'अष्टकवर्ग, योगिनी दशा, गोचर', en: 'Ashtakavarga, Yogini Dasha, Transits' },
              ].map((it, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-brand-gold-dark mt-2 flex-shrink-0" />
                  <span className={`text-brand-text-dark ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                    {isHi ? it.hi : it.en}
                  </span>
                </li>
              ))}
            </ul>
            <PremiumButton size="lg" onClick={onOpenForm}>
              <Sparkles className="w-4 h-4" />
              <span className={isHi ? 'font-hindi' : 'font-outfit'}>
                {isHi ? 'मेरी राशि का रहस्य खोलें' : 'Reveal My Rashi Secrets'}
              </span>
              <ArrowRight className="w-4 h-4" />
            </PremiumButton>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ZodiacWheelSection;
